import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, type FlatFileRecipeRow } from '../lib/api.js';

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Recipes are active on authoring — 'accepted' is shown as "active"; a held recipe
// (rare manual override) shows "held for review". The DB value stays 'accepted'.
const STATUS_STYLE: Record<string, { color: string; bg: string; label: string }> = {
  accepted: { color: 'var(--accent-green)', bg: 'rgba(13,207,184,0.12)', label: 'active' },
  pending_review: { color: 'var(--accent-yellow)', bg: 'rgba(255,209,102,0.12)', label: 'held for review' },
  superseded: { color: 'var(--text-dimmed)', bg: 'var(--bg-tertiary)', label: 'superseded' },
  rejected: { color: 'var(--accent-red)', bg: 'rgba(238,93,80,0.12)', label: 'rejected' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.superseded!;
  return (
    <span className="text-[10px] font-semibold rounded px-1.5 py-0.5" style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

function resourceTypesOf(specs: Array<Record<string, unknown>>): string[] {
  return [...new Set(specs.map((s) => (typeof s.resourceType === 'string' ? s.resourceType : '?')))];
}

function healthVerdict(health: Record<string, unknown> | null): { label: string; color: string } | null {
  if (!health) return null;
  const verdict = typeof health.verdict === 'string' ? health.verdict : null;
  if (verdict === 'PASS') return { label: 'health: PASS', color: 'var(--accent-green)' };
  if (verdict === 'FAIL') return { label: 'health: FAIL', color: 'var(--accent-red)' };
  return null;
}

// One file-shape lineage: the latest version headlined, older versions collapsed.
function RecipeCard({ versions, onChanged, ehrLabel }: { versions: FlatFileRecipeRow[]; onChanged: () => void; ehrLabel?: string }) {
  const [showSpecs, setShowSpecs] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const latest = versions[0]!; // list is version-desc within a lineage
  const rtypes = resourceTypesOf(latest.specs);
  const health = healthVerdict(latest.health);

  const accept = useMutation({
    mutationFn: (id: string) => api.acceptFlatFileRecipe(id),
    onSuccess: () => {
      toast.success('Recipe activated — reusable for this file shape');
      onChanged();
    },
    onError: (err) => toast.error(`Activate failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  return (
    <div className="glass rounded-2xl p-4" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {ehrLabel && (
              <span className="text-[10px] rounded px-1.5 py-0.5" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dimmed)', border: '1px solid var(--glass-border)' }}>
                {ehrLabel}
              </span>
            )}
            <span className="text-sm font-semibold text-text-primary truncate">{latest.label || latest.sourceSystem}</span>
            <StatusBadge status={latest.status} />
            <span className="text-[10px] text-text-dimmed">v{latest.version}</span>
            {health && <span className="text-[10px] font-semibold" style={{ color: health.color }}>{health.label}</span>}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {rtypes.map((rt) => (
              <span key={rt} className="text-[10px] rounded px-1.5 py-0.5" style={{ background: 'rgba(97,218,251,0.1)', color: 'var(--accent-blue)', border: '1px solid rgba(97,218,251,0.2)' }}>
                {rt}
              </span>
            ))}
          </div>
          <div className="mt-1.5 text-[11px] font-mono text-text-dimmed break-all">
            {latest.sourceSystem} · fp:{latest.headerFingerprint.slice(0, 12)}… · {latest.authoredBy}
          </div>
          <div className="mt-0.5 text-[10px] text-text-dimmed">
            last used {timeAgo(latest.lastUsedAt)} · {latest.acceptedAt ? `accepted ${timeAgo(latest.acceptedAt)}` : `scoped ${timeAgo(latest.scopedAt ?? latest.createdAt)}`}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {latest.status === 'pending_review' && (
            <button
              type="button"
              onClick={() => accept.mutate(latest.id)}
              disabled={accept.isPending}
              className="rounded-lg px-2.5 py-1 text-[10px] font-semibold text-white disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
            >
              {accept.isPending ? 'Activating…' : 'Activate'}
            </button>
          )}
          <button type="button" onClick={() => setShowSpecs((v) => !v)} className="text-[10px] font-semibold" style={{ color: 'var(--accent-blue)' }}>
            {showSpecs ? 'Hide spec' : 'View spec'}
          </button>
          {versions.length > 1 && (
            <button type="button" onClick={() => setShowVersions((v) => !v)} className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
              {showVersions ? 'Hide' : `${versions.length} versions`}
            </button>
          )}
        </div>
      </div>

      {latest.rationale && <div className="mt-2 text-[11px] text-text-secondary italic">{latest.rationale}</div>}

      {showSpecs && (
        <pre
          className="mt-3 rounded-lg p-3 text-[10px] font-mono overflow-x-auto max-h-80 overflow-y-auto"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {JSON.stringify(latest.specs, null, 2)}
        </pre>
      )}

      {showVersions && versions.length > 1 && (
        <div className="mt-3 border-t border-border pt-2 space-y-1">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between text-[10px]">
              <span className="text-text-dimmed">v{v.version} · {v.authoredBy}</span>
              <StatusBadge status={v.status} />
              <span className="text-text-dimmed">{timeAgo(v.acceptedAt ?? v.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Group recipes into file-shape lineages (sourceSystem, fingerprint), newest-first,
// and render one card per lineage. Shared by the per-EHR tab + the global page.
export function RecipeList({ recipes, onChanged, showEhr = false }: { recipes: FlatFileRecipeRow[]; onChanged: () => void; showEhr?: boolean }) {
  const lineages = new Map<string, FlatFileRecipeRow[]>();
  for (const r of recipes) {
    const key = `${r.sourceSystem}::${r.headerFingerprint}`;
    (lineages.get(key) ?? lineages.set(key, []).get(key)!).push(r);
  }
  for (const list of lineages.values()) list.sort((a, b) => b.version - a.version);
  const lineageList = [...lineages.values()].sort(
    (a, b) => new Date(b[0]!.updatedAt).getTime() - new Date(a[0]!.updatedAt).getTime(),
  );

  return (
    <div className="space-y-3">
      {lineageList.map((versions) => (
        <RecipeCard
          key={versions[0]!.id}
          versions={versions}
          onChanged={onChanged}
          ehrLabel={showEhr ? versions[0]!.ehrDisplayName : undefined}
        />
      ))}
    </div>
  );
}

export function recipeLineageCount(recipes: FlatFileRecipeRow[]): number {
  return new Set(recipes.map((r) => `${r.sourceSystem}::${r.headerFingerprint}`)).size;
}
